import { Skeleton } from "@/components/ui/skeleton";
import React from "react";

// Utility function to format seconds into HH:MM:SS format
const formatSeconds = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);

  return [hours, minutes, remainingSeconds]
    .map(val => val.toString().padStart(2, '0'))
    .join(':');
};

// Utility function to safely format numbers by removing trailing zeros
const formatNumber = (num: number): string => {
  const str = num.toFixed(2);
  if (!str.includes('.')) {
    return str;
  }

  // Split on decimal point
  const [whole, decimal] = str.split('.');

  // Remove trailing zeros from decimal part
  let trimmedDecimal = decimal;
  while (trimmedDecimal.endsWith('0')) {
    trimmedDecimal = trimmedDecimal.slice(0, -1);
  }

  // If decimal part is empty after trimming, return whole number
  return trimmedDecimal ? `${whole}.${trimmedDecimal}` : whole;
};

interface MetricDisplayProps {
  value: number | undefined | null;
  comparisonPercentage?: number;
  loading?: boolean;
  format?: 'percentage' | 'number' | 'datetime';
  invertColors?: boolean; // When true, positive values will be red and negative values will be green
}

const WidgetMetricDisplay: React.FC<MetricDisplayProps> = ({ value, comparisonPercentage, loading, format = 'number', invertColors = false }) => {
  if (loading) {
    return (
      <output aria-label="Loading" className="block w-full">
        <Skeleton className="h-16 w-full" />
      </output>
    );
  }

  const displayValue = value ?? 0;
  const hasComparison = comparisonPercentage !== undefined && comparisonPercentage !== null;

  let formattedValue: string;
  switch (format) {
    case 'percentage':
      formattedValue = `${formatNumber(displayValue)}%`;
      break;
    case 'datetime':
      formattedValue = value ? formatSeconds(value) : "00:00:00";
      break;
    default:
      formattedValue = formatNumber(displayValue);
  }

  let comparisonColor = "text-gray-600 dark:text-white";
  if (hasComparison && comparisonPercentage > 0) {
    comparisonColor = invertColors ? "text-red-600" : "text-success";
  } else if (hasComparison && comparisonPercentage < 0) {
    comparisonColor = invertColors ? "text-success" : "text-red-600";
  }

  return (
    <>
      <div className="text-[24px] font-[700] text-primary dark:text-white my-3">{formattedValue}</div>
      <div className="text-[12px] font-[500]">
        {hasComparison ? (
          <>
            <span className={comparisonColor}>
              {comparisonPercentage > 0 ? "+" : ""}{formatNumber(comparisonPercentage)}%
            </span>
            {" vs previous period"}
          </>
        ) : (
          <span className="text-primary dark:text-white">No previous data to compare</span>
        )}
      </div>
    </>
  );
};

export default WidgetMetricDisplay;
